import {
  EnumProductCustomFieldsType,
  EnumSurveyQuestionType,
  type Product,
  type ProductCustomFields,
  type SurveyQuestion,
} from "gql/generated";
import _ from "lodash";
import type {
  CartCustomFieldResponse,
  CartItem,
} from "widgets/booker/contexts/CartContextProvider/cartContextTypes";

const QUESTION_TYPE_BY_FIELD_TYPE: Record<
  EnumProductCustomFieldsType,
  EnumSurveyQuestionType
> = {
  [EnumProductCustomFieldsType.ShortText]: EnumSurveyQuestionType.ShortText,
  [EnumProductCustomFieldsType.LongText]: EnumSurveyQuestionType.LongText,
  [EnumProductCustomFieldsType.Select]: EnumSurveyQuestionType.Select,
  [EnumProductCustomFieldsType.Boolean]: EnumSurveyQuestionType.Boolean,
};

export const getAddOnCustomFields = (addOn: Product | undefined) =>
  _.compact(addOn?.customFields).filter(field => !!field._id);

/** Present a product custom field through the survey question renderer. */
export const toSurveyQuestion = (field: ProductCustomFields): SurveyQuestion =>
  ({
    _id: String(field._id),
    name: field.label,
    type: QUESTION_TYPE_BY_FIELD_TYPE[field.type],
    required: field.required,
    isActive: true,
    // A Boolean question with options renders as a multi-checkbox group;
    // without options it is a single yes/no checkbox.
    options:
      field.type === EnumProductCustomFieldsType.Select ||
      field.type === EnumProductCustomFieldsType.Boolean
        ? _.compact(field.options).map(option => ({
            _id: option._id,
            label: option.label,
            isActive: true,
          }))
        : [],
  }) as unknown as SurveyQuestion;

const isAnswered = (
  field: ProductCustomFields,
  response: string[] | undefined,
) => {
  const values = (response ?? []).map(value => value.trim()).filter(Boolean);
  if (field.type === EnumProductCustomFieldsType.Boolean) {
    return _.compact(field.options).length > 0
      ? values.length > 0
      : values[0] === "true";
  }
  return values.length > 0;
};

const areAnswersComplete = (
  addOn: Product | undefined,
  answers: CartCustomFieldResponse[] | undefined,
) =>
  getAddOnCustomFields(addOn).every(
    field =>
      (!field.required && !field.chargePerChoice) ||
      isAnswered(
        field,
        answers?.find(answer => answer.fieldId === String(field._id))?.response,
      ),
  );

export const areAddOnDetailsComplete = (
  addOn: Product | undefined,
  item: Pick<CartItem, "customFieldResponses" | "businessScopes"> | undefined,
) =>
  item?.businessScopes?.length
    ? item.businessScopes.every(scope =>
        areAnswersComplete(addOn, scope.customFieldResponses),
      )
    : areAnswersComplete(addOn, item?.customFieldResponses);

export const setCustomFieldResponse = (
  current: CartCustomFieldResponse[] | undefined,
  fieldId: string,
  response: string[],
): CartCustomFieldResponse[] => [
  ...(current ?? []).filter(answer => answer.fieldId !== fieldId),
  { fieldId, response },
];
